import { useState, useEffect } from "react";
import { settingsAPI, glAccountsAPI, jobsAPI, usersAPI, syncAPI, mercuryAPI, qboAPI, mercuryUsersAPI, vendorsAPI } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Gear, Plus, Trash, Database, CreditCard, UserCircle, ArrowsClockwise, PencilSimple, Check, Copy, MagnifyingGlass, CaretDown, CaretRight, Eye, Lightning, EnvelopeSimple, Key } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ROLE_OPTIONS, roleLabel } from "../lib/roles";
import { AutopilotRulesPanel } from "../components/AutopilotRulesPanel";
import { SearchableSelect } from "../components/SearchableSelect";
import { buildGlItems } from "../lib/glAccounts";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [glAccounts, setGlAccounts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Integration form state
  const [qboClientId, setQboClientId] = useState("");
  const [qboClientSecret, setQboClientSecret] = useState("");
  const [qboRealmId, setQboRealmId] = useState("");
  const [qboEnv, setQboEnv] = useState("production");
  const [jtApiKey, setJtApiKey] = useState("");
  const [mercuryApiKey, setMercuryApiKey] = useState("");

  // New GL / Job form
  const [newGL, setNewGL] = useState({ name: "", account_type: "Expense", account_number: "" });
  const [newJob, setNewJob] = useState({ name: "", number: "", customer: "" });

  // User edit dialog
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const navigate = useNavigate();
  const { user: currentUser, impersonate } = useAuth();
  const { resetPushSubscriptions } = useNotifications();

  const handlePreviewUser = async (u) => {
    if (u.id === currentUser?.id) {
      toast.info("That's you — nothing to preview");
      return;
    }
    if (u.status !== "active") {
      toast.error("Can only preview active users");
      return;
    }
    const res = await impersonate(u.id);
    if (res.success) {
      toast.success(`Now previewing as ${u.name || u.email}`);
      navigate("/");
    } else {
      toast.error(res.error || "Failed to start preview");
    }
  };

  // Mercury webhook config
  const [webhookConfig, setWebhookConfig] = useState({ webhook_url: "", has_secret: false });
  const [webhookSecretInput, setWebhookSecretInput] = useState("");
  const [webhookHealth, setWebhookHealth] = useState(null);

  // Tombstoned (soft-deleted) mercury transactions — recovery UI
  const [tombstoned, setTombstoned] = useState([]);
  const [tombstonedLoading, setTombstonedLoading] = useState(false);
  const [restoringId, setRestoringId] = useState("");

  const refreshTombstoned = async () => {
    setTombstonedLoading(true);
    try {
      const { data } = await mercuryAPI.listTombstoned(30);
      setTombstoned(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load hidden transactions");
    } finally {
      setTombstonedLoading(false);
    }
  };

  const restoreTombstoned = async (row) => {
    setRestoringId(row.id);
    try {
      await mercuryAPI.untombstone(row.id);
      toast.success(`Restored ${row.merchant} $${Number(row.amount).toFixed(2)}`);
      setTombstoned((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally {
      setRestoringId("");
    }
  };

  const refreshWebhookHealth = async () => {
    try {
      const { data } = await mercuryAPI.webhookHealth();
      setWebhookHealth(data);
    } catch { /* admin-only — silently ignore for non-admins */ }
  };

  // QuickBooks status
  const [qboStatus, setQboStatus] = useState({ connected: false, redirect_uri: "", realm_id: "", company_name: "" });
  const [qboBusy, setQboBusy] = useState("");

  // Mercury team list
  const [mercuryUsers, setMercuryUsers] = useState([]);
  // QBO Customers/Projects (for Job mapping dropdown + Customers tab)
  const [qboCustomers, setQboCustomers] = useState([]);
  // QBO Vendors (for Vendors tab)
  const [qboVendors, setQboVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [inviteDialog, setInviteDialog] = useState({ open: false, employee: null, email: "", name: "", role: "submitter", sending: false, result: null });

  // GL accounts search + collapsed groups
  const [glSearch, setGlSearch] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const [settingsRes, glRes, jobsRes, usersRes, webhookRes, webhookHealthRes, qboRes, mercuryUsersRes, qboCustomersRes, vendorsRes] = await Promise.all([
          settingsAPI.get().catch(() => ({ data: {} })),
          glAccountsAPI.list(),
          jobsAPI.list(),
          usersAPI.list().catch(() => ({ data: { users: [] } })),
          mercuryAPI.webhookConfig().catch(() => ({ data: { webhook_url: "", has_secret: false } })),
          mercuryAPI.webhookHealth().catch(() => ({ data: null })),
          qboAPI.status().catch(() => ({ data: { connected: false, redirect_uri: "" } })),
          mercuryUsersAPI.list().catch(() => ({ data: { users: [] } })),
          qboAPI.listCustomers().catch(() => ({ data: { customers: [] } })),
          vendorsAPI.listAll().catch(() => ({ data: { vendors: [] } })),
        ]);
        setSettings(settingsRes.data);
        // Populate input fields with masked values from saved settings
        const s = settingsRes.data;
        if (s.qbo_client_id) setQboClientId(s.qbo_client_id);
        if (s.qbo_client_secret) setQboClientSecret(s.qbo_client_secret);
        if (s.qbo_realm_id) setQboRealmId(s.qbo_realm_id);
        if (s.qbo_environment) setQboEnv(s.qbo_environment);
        if (s.jobtread_api_key) setJtApiKey(s.jobtread_api_key);
        if (s.mercury_api_key) setMercuryApiKey(s.mercury_api_key);
        setGlAccounts(glRes.data.accounts);
        setJobs(jobsRes.data.jobs);
        setUsers(usersRes.data.users || []);
        setWebhookConfig(webhookRes.data);
        setWebhookHealth(webhookHealthRes.data);
        setQboStatus(qboRes.data);
        setMercuryUsers(mercuryUsersRes.data.users || []);
        setQboCustomers(qboCustomersRes.data.customers || []);
        setQboVendors(vendorsRes.data.vendors || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();

    // Handle OAuth callback redirects
    const params = new URLSearchParams(window.location.search);
    if (params.get("qbo_connected")) {
      toast.success("QuickBooks connected successfully");
      window.history.replaceState({}, "", window.location.pathname);
    }
    const err = params.get("qbo_error");
    if (err) {
      toast.error(`QuickBooks connection failed: ${err}${params.get("detail") ? ` — ${params.get("detail")}` : ""}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const saveIntegrations = async () => {
    try {
      const data = {};
      if (qboClientId) data.qbo_client_id = qboClientId;
      if (qboClientSecret) data.qbo_client_secret = qboClientSecret;
      if (qboRealmId) data.qbo_realm_id = qboRealmId;
      if (jtApiKey) data.jobtread_api_key = jtApiKey;
      if (mercuryApiKey) data.mercury_api_key = mercuryApiKey;
      await settingsAPI.update(data);
      toast.success("Integration settings saved");
      // Reload to get fresh masked values
      const { data: updated } = await settingsAPI.get().catch(() => ({ data: {} }));
      setSettings(updated);
      if (updated.qbo_client_id) setQboClientId(updated.qbo_client_id);
      if (updated.qbo_client_secret) setQboClientSecret(updated.qbo_client_secret);
      if (updated.qbo_realm_id) setQboRealmId(updated.qbo_realm_id);
      if (updated.jobtread_api_key) setJtApiKey(updated.jobtread_api_key);
      if (updated.mercury_api_key) setMercuryApiKey(updated.mercury_api_key);
    } catch (e) {
      toast.error("Failed to save settings");
    }
  };

  const purgeSimulated = async () => {
    if (!window.confirm("Permanently delete ALL training/simulated transactions, expenses, and test notifications? Real Mercury and QBO data are NOT touched. This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/purge-simulated");
      toast.success(`Purged ${data.transactions_deleted} txn(s), ${data.expenses_deleted} expense(s), ${data.notifications_deleted} notification(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    }
  };

  const purgePreCutoff = async () => {
    if (!window.confirm("Delete all Mercury transactions dated before the launch cutoff (May 1, 2026)? Transactions already linked to an expense will be skipped to avoid orphaning. This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/purge-pre-cutoff-transactions");
      toast.success(`${data.message}${data.skipped_linked_to_expense ? ` — skipped ${data.skipped_linked_to_expense} already linked to an expense` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    }
  };

  const purgeZeroAmount = async () => {
    if (!window.confirm("Delete all $0 phantom Mercury transactions? These are Mercury pre-auth/memo events that should never have been ingested. Skips any that are already linked to an expense.")) return;
    try {
      const { data } = await api.post("/admin/purge-zero-amount-transactions");
      toast.success(`${data.message}${data.skipped_linked_to_expense ? ` — skipped ${data.skipped_linked_to_expense} already linked` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    }
  };

  const handleResetPush = async () => {
    if (!window.confirm("Reset push notifications for this account? This wipes every device's push subscription and re-registers this device. Use it if you're getting duplicate notifications.")) return;
    const res = await resetPushSubscriptions();
    if (res.success) toast.success("Push notifications reset — this device re-subscribed.");
    else toast.error(res.error || "Reset failed");
  };


  const addGLAccount = async () => {
    if (!newGL.name) return;
    try {
      await glAccountsAPI.create(newGL);
      const { data } = await glAccountsAPI.list();
      setGlAccounts(data.accounts);
      setNewGL({ name: "", account_type: "Expense", account_number: "" });
      toast.success("GL account added");
    } catch (e) { toast.error("Failed to add account"); }
  };

  const deleteGL = async (id) => {
    try {
      await glAccountsAPI.delete(id);
      setGlAccounts((prev) => prev.filter((a) => a.id !== id));
      toast.success("Account removed");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const toggleGLVisibility = async (id) => {
    try {
      const { data } = await glAccountsAPI.toggleVisibility(id);
      setGlAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, visible: data.visible } : a)));
    } catch (e) { toast.error("Failed to toggle visibility"); }
  };

  const toggleGLAutoApprove = async (id) => {
    try {
      const { data } = await glAccountsAPI.toggleAutoApprove(id);
      setGlAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, auto_approve: data.auto_approve } : a)));
    } catch (e) { toast.error("Failed to toggle auto-approve"); }
  };

  const bulkSetGLVisibility = async (visible) => {
    try {
      await glAccountsAPI.bulkVisibility([], visible);
      setGlAccounts((prev) => prev.map((a) => ({ ...a, visible })));
      toast.success(visible ? "All accounts shown" : "All accounts hidden");
    } catch (e) { toast.error("Bulk update failed"); }
  };

  const syncGLFromQBO = async () => {
    if (!qboStatus.connected) {
      toast.error("Connect QuickBooks first (Integrations tab)");
      return;
    }
    setSyncing("qbo-gl");
    try {
      const { data } = await qboAPI.syncAccounts();
      toast.success(data.message);
      const { data: refreshed } = await glAccountsAPI.list();
      setGlAccounts(refreshed.accounts);
      const { data: status } = await qboAPI.status();
      setQboStatus(status);
    } catch (e) { toast.error(e.response?.data?.detail || "QBO sync failed"); }
    setSyncing("");
  };

  const syncQboVendors = async () => {
    if (!qboStatus.connected) {
      toast.error("Connect QuickBooks first (Integrations tab)");
      return;
    }
    setSyncing("qbo-vendors");
    try {
      const { data } = await qboAPI.syncVendors();
      toast.success(data.message);
      const { data: refreshed } = await vendorsAPI.listAll();
      setQboVendors(refreshed.vendors || []);
    } catch (e) { toast.error(e.response?.data?.detail || "QBO vendor sync failed"); }
    setSyncing("");
  };

  const addJob = async () => {
    if (!newJob.name) return;
    try {
      await jobsAPI.create(newJob);
      const { data } = await jobsAPI.list();
      setJobs(data.jobs);
      setNewJob({ name: "", number: "", customer: "" });
      toast.success("Job added");
    } catch (e) { toast.error("Failed to add job"); }
  };

  const deleteJob = async (id) => {
    try {
      await jobsAPI.delete(id);
      setJobs((prev) => prev.filter((j) => j.id !== id));
      toast.success("Job removed");
    } catch (e) { toast.error("Failed to delete"); }
  };

  // User management
  const openEditUser = (u) => {
    setEditUser(u);
    // Drop the implicit "submitter" role from the editable set — it's always on.
    const roles = (u.roles || []).filter((r) => r !== "submitter");
    setEditForm({
      name: u.name || "",
      roles,
      status: u.status || "active",
      mercury_user_id: u.mercury_user_id || "",
      auto_approve: !!u.auto_approve,
    });
  };

  const toggleEditRole = (role) => {
    setEditForm((p) => {
      const set = new Set(p.roles || []);
      if (set.has(role)) {
        set.delete(role);
      } else {
        // Accountant is a strict standalone read-only role. Picking it clears
        // every other selection; picking any other role clears Accountant.
        if (role === "accountant") {
          set.clear();
          set.add("accountant");
        } else {
          set.delete("accountant");
          set.add(role);
        }
      }
      return { ...p, roles: [...set] };
    });
  };

  const saveUserMapping = async () => {
    if (!editUser) return;
    try {
      // If admin selected Accountant, persist JUST that role (no implicit
      // submitter). Otherwise re-attach the implicit submitter role on save.
      const formRoles = editForm.roles || [];
      const roles = formRoles.includes("accountant")
        ? ["accountant"]
        : [...new Set([...formRoles, "submitter"])];
      const payload = { ...editForm, roles };
      // Accountants can never auto-approve — strip the flag locally to match
      // the backend guardrail and keep the UI from showing stale state.
      if (roles.includes("accountant")) payload.auto_approve = false;
      const { data } = await usersAPI.update(editUser.id, payload);
      setUsers((prev) => prev.map((u) => u.id === editUser.id ? data : u));
      toast.success("User updated");
      setEditUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update user");
    }
  };

  const syncQBO = async () => {
    try {
      const { data } = await usersAPI.syncQBO();
      toast.info(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  };

  const connectQbo = async () => {
    // Save Client ID + Secret + environment first if user has typed new values
    setQboBusy("connect");
    try {
      const payload = { qbo_environment: qboEnv };
      if (qboClientId && !qboClientId.startsWith("***")) payload.qbo_client_id = qboClientId;
      if (qboClientSecret && !qboClientSecret.startsWith("***")) payload.qbo_client_secret = qboClientSecret;
      await settingsAPI.update(payload);
      const { data } = await qboAPI.connect();
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start QuickBooks OAuth");
      setQboBusy("");
    }
  };

  const disconnectQbo = async () => {
    if (!window.confirm("Disconnect from QuickBooks Online? You'll need to reconnect before pushing any future expenses.")) return;
    setQboBusy("disconnect");
    try {
      await qboAPI.disconnect();
      setQboStatus({ connected: false, redirect_uri: qboStatus.redirect_uri });
      toast.success("Disconnected from QuickBooks");
    } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
    setQboBusy("");
  };

  const testQbo = async () => {
    setQboBusy("test");
    try {
      const { data } = await qboAPI.test();
      setQboStatus((p) => ({ ...p, company_name: data.company_name }));
      toast.success(`Connected: ${data.company_name || "OK"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    setQboBusy("");
  };

  const syncMercuryUsers = async () => {
    setSyncing("mercury-users");
    try {
      const { data } = await mercuryUsersAPI.sync();
      toast.success(data.message);
      const { data: refreshed } = await mercuryUsersAPI.list();
      setMercuryUsers(refreshed.users || []);
      // Auto-link may have attached cards to the current admin — refresh users too
      if (data.auto_linked) {
        const { data: ur } = await usersAPI.list();
        setUsers(ur.users || []);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Mercury team sync failed"); }
    setSyncing("");
  };

  const openInviteDialog = (mercuryUser) => {
    setInviteDialog({
      open: true,
      employee: mercuryUser,
      email: mercuryUser.email || "",
      name: mercuryUser.display_name || `${mercuryUser.first_name || ""} ${mercuryUser.last_name || ""}`.trim(),
      role: mercuryUser.organization_role === "administrator" ? "administrator" : "submitter",
      sending: false,
      result: null,
    });
  };

  const sendInvite = async () => {
    if (!inviteDialog.employee) return;
    setInviteDialog((p) => ({ ...p, sending: true }));
    try {
      const { data } = await usersAPI.invite({
        mercury_user_id: inviteDialog.employee.mercury_user_id,
        email: inviteDialog.email,
        name: inviteDialog.name,
        role: inviteDialog.role,
        send_invite: true,
      });
      setUsers((prev) => [...prev, data.user]);
      setMercuryUsers((prev) => prev.map((m) =>
        m.mercury_user_id === inviteDialog.employee.mercury_user_id ? { ...m, has_expenser_account: true, expenser_email: data.user.email } : m
      ));
      setInviteDialog((p) => ({ ...p, sending: false, result: data }));
      if (data.email?.sent) {
        toast.success(`Invite emailed to ${data.user.email}`);
      } else {
        toast.warning(`User created — copy the invite link below (email could not send: ${data.email?.reason || "n/a"})`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
      setInviteDialog((p) => ({ ...p, sending: false }));
    }
  };

  const resendInvite = async (userId) => {
    try {
      const { data } = await usersAPI.resendInvite(userId);
      if (data.email?.sent) {
        toast.success("Invite re-sent");
      } else {
        toast.info(`Copy link: ${data.invite_url}`);
        navigator.clipboard?.writeText(data.invite_url).catch(() => {});
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Admin trigger: email this user a password-reset link. Uses the same
  // token machinery as the self-service "Forgot password" flow.
  const sendPasswordReset = async (userId, email) => {
    if (!window.confirm(`Send a password-reset link to ${email}?`)) return;
    try {
      const { data } = await usersAPI.sendPasswordReset(userId);
      if (data.email?.sent) {
        toast.success(`Reset link emailed to ${email}`);
      } else {
        // Resend down — fall back to manual share.
        toast.info("Email send failed — copy link instead", { duration: 7000 });
        navigator.clipboard?.writeText(data.reset_url).catch(() => {});
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Admin trigger: skip the email entirely — set a random temp password,
  // show it once, and force the user to change it on next login. Useful
  // when the user can't access their email (e.g. lost employee, fast
  // recovery, secure-channel share).
  const setTempPassword = async (userId, email) => {
    if (!window.confirm(
      `Generate a temporary password for ${email}?\n\n` +
      `They'll be forced to change it on next sign-in. ` +
      `The temp password will be shown ONCE — copy it somewhere safe before closing.`
    )) return;
    try {
      const { data } = await usersAPI.setTempPassword(userId);
      // Copy to clipboard for safe sharing via secure channel.
      navigator.clipboard?.writeText(data.temp_password).catch(() => {});
      // Persistent toast since this is shown only once.
      toast.success(`Temporary password copied to clipboard`, {
        description: `Password: ${data.temp_password} — share securely with ${email}. They'll be forced to change it on next sign-in.`,
        duration: 30000,
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeUserCard = async (userId, cardId, lastFour) => {
    if (!window.confirm(`Remove card ****${lastFour || ""} from this user?`)) return;
    try {
      const { data } = await usersAPI.removeCard(userId, cardId);
      setUsers((prev) => prev.map((u) => (u.id === userId ? data : u)));
      toast.success("Card removed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // (QBO employee status is managed directly in QuickBooks — no in-app toggle.)

  // Discovered cards from Mercury
  const syncMercuryTxns = async () => {
    setSyncing("mercury-txns");
    try {
      const { data } = await syncAPI.mercuryTransactions();
      // Build a richer toast describing why the count is what it is —
      // helps when Mercury shows more txns than the backfill imports.
      const detailLines = [];
      if (data.scanned_total != null) detailLines.push(`${data.scanned_total} scanned`);
      if (data.skipped_already_in_db) detailLines.push(`${data.skipped_already_in_db} already in DB`);
      if (data.skipped_not_card_kind) detailLines.push(`${data.skipped_not_card_kind} non-card (ACH/transfer/etc.)`);
      if (data.skipped_zero_amount) detailLines.push(`${data.skipped_zero_amount} zero-amount`);
      toast.success(data.message, {
        description: detailLines.length ? detailLines.join(" · ") : undefined,
        duration: 8000,
      });
      // Also log full breakdown to console for power users
      console.log("Mercury backfill detail:", data);
    } catch (e) { toast.error(e.response?.data?.detail || "Mercury transaction sync failed"); }
    setSyncing("");
  };

  const saveWebhookSecret = async () => {
    if (!webhookSecretInput.trim()) {
      toast.error("Paste the secret Mercury gave you when you created the webhook");
      return;
    }
    try {
      await settingsAPI.update({ mercury_webhook_secret: webhookSecretInput.trim() });
      toast.success("Webhook secret saved. Live card swipes will now be verified.");
      setWebhookSecretInput("");
      setWebhookConfig((p) => ({ ...p, has_secret: true }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save secret"); }
  };

  const clearWebhookSecret = async () => {
    if (!window.confirm("Clear the stored webhook secret? The endpoint will accept unsigned events until you save a new one. Use this before creating the webhook in Mercury so their test ping returns 200.")) return;
    try {
      await mercuryAPI.clearWebhookSecret();
      toast.success("Secret cleared. You can now create the webhook in Mercury.");
      setWebhookConfig((p) => ({ ...p, has_secret: false }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clear secret"); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed")
    );
  };

  const [syncing, setSyncing] = useState("");

  const syncJTJobs = async () => {
    setSyncing("jobs");
    try {
      const { data } = await syncAPI.jobtreadJobs();
      toast.success(data.message);
      // Reload jobs
      const { data: jobsData } = await jobsAPI.list();
      setJobs(jobsData.jobs);
    } catch (e) { toast.error(e.response?.data?.detail || "Job sync failed"); }
    setSyncing("");
  };

  const syncJTBudgetItems = async () => {
    setSyncing("budget");
    try {
      const { data } = await syncAPI.jobtreadBudgetItems();
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Budget item sync failed"); }
    setSyncing("");
  };

  const syncQboCustomers = async () => {
    setSyncing("qbo-customers");
    try {
      const { data } = await qboAPI.syncCustomers();
      toast.success(data.message);
      const [{ data: cust }, { data: jobsData }] = await Promise.all([
        qboAPI.listCustomers(),
        jobsAPI.list(),
      ]);
      setQboCustomers(cust.customers || []);
      setJobs(jobsData.jobs || []);
    } catch (e) { toast.error(e.response?.data?.detail || "QBO customer sync failed"); }
    setSyncing("");
  };

  const updateJobCustomer = async (jobId, qboCustomerId) => {
    try {
      const { data } = await jobsAPI.update(jobId, { qbo_customer_id: qboCustomerId });
      setJobs((prev) => prev.map((j) => (j.id === jobId ? data : j)));
      toast.success(qboCustomerId ? "Job linked to QBO customer" : "QBO link cleared");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update job"); }
  };

  const updateCardQboAccount = async (userId, card, qboAccountId) => {
    try {
      const { data } = await usersAPI.addCard(userId, {
        card_id: card.card_id,
        last_four: card.last_four,
        network: card.network,
        card_type: card.card_type,
        qbo_account_id: qboAccountId,
      });
      setUsers((prev) => prev.map((u) => (u.id === userId ? data : u)));
      setEditUser((p) => (p && p.id === userId ? { ...p, cards: data.cards } : p));
      toast.success(qboAccountId ? "Card linked to QBO account" : "QBO link cleared");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update card"); }
  };

  // Manually attach a card by last-four to a user. Used when Mercury's card
  // discovery doesn't return a card (e.g. cancelled/expired card belonging
  // to a former employee) but an expense referencing that card still lands
  // in Expenser (e.g. a delayed refund). Generates a synthetic card_id
  // prefixed `manual-` so it's clear this wasn't discovered via the API.
  const [manualCard, setManualCard] = useState({ last_four: "", card_type: "credit" });
  const addManualCard = async () => {
    const lf = (manualCard.last_four || "").trim();
    if (!/^\d{4}$/.test(lf)) {
      toast.error("Enter exactly 4 digits (e.g. 3491)");
      return;
    }
    try {
      const { data } = await usersAPI.addCard(editUser.id, {
        card_id: `manual-${lf}-${Date.now()}`,
        last_four: lf,
        network: "manual",
        card_type: manualCard.card_type,
        qbo_account_id: "",
      });
      setUsers((prev) => prev.map((u) => (u.id === editUser.id ? data : u)));
      setEditUser(data);
      setManualCard({ last_four: "", card_type: "credit" });
      toast.success(`Card ****${lf} attached — now set its QBO account below`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to attach card");
    }
  };

  if (loading) return <div className="p-8 animate-pulse text-zinc-400">Loading settings...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Settings
        </h1>
        <p className="text-sm text-zinc-500 mt-0.5">Manage users, integrations, GL accounts, jobs, customers, and vendors</p>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="bg-zinc-100 rounded-sm p-0.5 flex-wrap h-auto">
          <TabsTrigger value="users" className="rounded-sm text-xs" data-testid="tab-users">Users & Cards</TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-sm text-xs" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="gl-accounts" className="rounded-sm text-xs" data-testid="tab-gl-accounts">GL Accounts</TabsTrigger>
          <TabsTrigger value="jobs" className="rounded-sm text-xs" data-testid="tab-jobs">Jobs</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-sm text-xs" data-testid="tab-customers">QBO Customers</TabsTrigger>
          <TabsTrigger value="vendors" className="rounded-sm text-xs" data-testid="tab-vendors">QBO Vendors</TabsTrigger>
          <TabsTrigger value="autopilot" className="rounded-sm text-xs" data-testid="tab-autopilot">Autopilot</TabsTrigger>
        </TabsList>

        <TabsContent value="autopilot">
          <AutopilotRulesPanel />
        </TabsContent>

        {/* Users & Cards Tab */}
        <TabsContent value="users">
          <div className="space-y-6">
            {/* Mercury actions */}
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={syncMercuryTxns} disabled={syncing === "mercury-txns"} data-testid="sync-mercury-txns-btn"
                className="rounded-sm text-xs gap-1.5 border-zinc-200" title="One-time historical backfill. Live transactions arrive via webhook.">
                <ArrowsClockwise size={14} className={`text-blue-500 ${syncing === "mercury-txns" ? "animate-spin" : ""}`} />
                {syncing === "mercury-txns" ? "Backfilling..." : "Backfill Recent Transactions"}
              </Button>
              <p className="text-[11px] text-zinc-400 self-center">
                Live swipes arrive via webhook. Cards auto-link when you Sync Mercury Team below.
              </p>
            </div>

            {/* Expenser users */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                  Expenser Users — {users.length}
                </h3>
              </div>
              <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
                <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                  <div className="col-span-3">Name / Email</div>
                  <div className="col-span-2">Role / Status</div>
                  <div className="col-span-2">Mercury Link</div>
                  <div className="col-span-4">Cards</div>
                  <div className="col-span-1 text-right">Edit</div>
                </div>
                <div className="divide-y divide-zinc-100">
                  {users.map((u) => {
                    const userCards = u.cards || [];
                    return (
                      <div key={u.id} className="px-4 py-3 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center hover:bg-zinc-50 transition-colors" data-testid={`user-row-${u.id}`}>
                        <div className="col-span-3">
                          <p className="text-sm font-medium text-zinc-800">{u.name}</p>
                          <p className="text-xs text-zinc-400 truncate">{u.email}</p>
                        </div>
                        <div className="col-span-2 flex gap-1 flex-wrap mt-1 sm:mt-0">
                          {(u.roles || []).filter((r) => r !== "submitter").length === 0 ? (
                            <Badge className="text-[9px] uppercase rounded-sm bg-zinc-100 text-zinc-500 border-zinc-200">
                              Submitter
                            </Badge>
                          ) : (
                            (u.roles || []).filter((r) => r !== "submitter").map((r) => (
                              <Badge key={r} className={`text-[9px] uppercase rounded-sm ${
                                r === "administrator" ? "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20"
                                : r === "approver" ? "bg-purple-50 text-purple-700 border-purple-200"
                                : r === "accountant" ? "bg-amber-50 text-amber-800 border-amber-200"
                                : "bg-zinc-100 text-zinc-500 border-zinc-200"
                              }`}>
                                {roleLabel(r)}
                              </Badge>
                            ))
                          )}
                          {u.auto_approve && (
                            <Badge className="text-[9px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`user-auto-approve-badge-${u.id}`} title="Submissions from this user auto-approve">
                              auto
                            </Badge>
                          )}
                          <Badge className={`text-[9px] uppercase rounded-sm ${
                            u.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : u.status === "invited" ? "bg-amber-100 text-amber-700 border-amber-200"
                            : "bg-red-100 text-red-600 border-red-200"
                          }`}>
                            {u.status || "active"}
                          </Badge>
                        </div>
                        <div className="col-span-2 mt-1 sm:mt-0">
                          {u.mercury_user_id ? (
                            <Badge className="text-[9px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">
                              <Check size={9} weight="bold" className="mr-0.5" /> Mercury
                            </Badge>
                          ) : (
                            <span className="text-xs text-zinc-300">Not linked</span>
                          )}
                        </div>
                        <div className="col-span-4 mt-1 sm:mt-0">
                          {userCards.length === 0 ? (
                            <span className="text-xs text-zinc-300">No cards assigned</span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {userCards.map((c) => (
                                <div key={c.card_id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-zinc-100 border border-zinc-200 group" data-testid={`user-card-chip-${c.card_id}`}>
                                  <CreditCard size={11} className="text-zinc-500" />
                                  <span className="text-[11px] font-mono font-bold text-zinc-700">****{c.last_four || "?"}</span>
                                  {c.card_type === "credit" && <span className="text-[8px] uppercase tracking-wider text-purple-600 font-bold">credit</span>}
                                  {c.card_type === "deposit" && <span className="text-[8px] uppercase tracking-wider text-blue-600 font-bold">debit</span>}
                                  {!c.qbo_account_id && <span className="text-[8px] uppercase tracking-wider text-amber-600 font-bold" title="No QBO bank account mapped — approved expenses on this card can't push to QBO yet">⚠ qbo</span>}
                                  <button
                                    onClick={() => removeUserCard(u.id, c.card_id, c.last_four)}
                                    data-testid={`remove-user-card-${c.card_id}`}
                                    className="ml-0.5 text-zinc-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Remove card"
                                  >
                                    <Trash size={11} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="col-span-1 text-right mt-1 sm:mt-0 flex justify-end gap-1">
                          {u.status === "active" && u.id !== currentUser?.id && (
                            <Button variant="ghost" size="sm" onClick={() => handlePreviewUser(u)} data-testid={`preview-user-${u.id}`}
                              className="h-7 px-2 text-zinc-400 hover:text-[#002FA7]" title="Preview as this user (impersonate)">
                              <Eye size={14} />
                            </Button>
                          )}
                          {u.status === "invited" && !u.has_password && (
                            <Button variant="ghost" size="sm" onClick={() => resendInvite(u.id)} data-testid={`resend-invite-${u.id}`}
                              className="h-7 px-2 text-amber-500 hover:text-amber-600" title="Resend invite email">
                              <ArrowsClockwise size={14} />
                            </Button>
                          )}
                          {u.status === "active" && u.has_password && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => sendPasswordReset(u.id, u.email)} data-testid={`send-reset-${u.id}`}
                                className="h-7 px-2 text-zinc-500 hover:text-[#002FA7]" title="Email password-reset link">
                                <EnvelopeSimple size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setTempPassword(u.id, u.email)} data-testid={`set-temp-${u.id}`}
                                className="h-7 px-2 text-zinc-500 hover:text-amber-600" title="Set a temporary password (forces change on next login)">
                                <Key size={14} />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}
                            className="h-7 px-2 text-zinc-500 hover:text-zinc-700">
                            <PencilSimple size={14} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {users.length === 0 && (
                    <div className="p-8 text-center">
                      <UserCircle size={32} className="text-zinc-200 mx-auto mb-2" />
                      <p className="text-sm text-zinc-400">No users yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mercury team */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                    Mercury Team — {mercuryUsers.length}
                  </h3>
                  <p className="text-[10px] text-zinc-400 mt-0.5">Pulled from Mercury — users keep their company email and the cards already issued in their name auto-attach on invite.</p>
                </div>
                <Button onClick={syncMercuryUsers} disabled={syncing === "mercury-users"}
                  data-testid="sync-mercury-users-btn"
                  className="bg-[#1A1A1A] hover:bg-zinc-700 text-white rounded-sm text-xs gap-1.5 h-7 disabled:opacity-40">
                  <ArrowsClockwise size={12} weight="bold" className={syncing === "mercury-users" ? "animate-spin" : ""} />
                  {syncing === "mercury-users" ? "Syncing..." : "Sync from Mercury"}
                </Button>
              </div>
              <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
                <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                  <div className="col-span-3">Name</div>
                  <div className="col-span-3">Email</div>
                  <div className="col-span-2">Mercury Role</div>
                  <div className="col-span-2">Cards</div>
                  <div className="col-span-2 text-right">Expenser Access</div>
                </div>
                <div className="divide-y divide-zinc-100">
                  {mercuryUsers.length === 0 ? (
                    <div className="p-8 text-center">
                      <UserCircle size={32} className="text-zinc-200 mx-auto mb-2" />
                      <p className="text-sm text-zinc-400">
                        Click &quot;Sync from Mercury&quot; to pull your team
                      </p>
                    </div>
                  ) : mercuryUsers.map((m) => (
                    <div key={m.mercury_user_id} className="px-4 py-3 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center hover:bg-zinc-50 transition-colors" data-testid={`mercury-user-row-${m.mercury_user_id}`}>
                      <div className="col-span-3">
                        <p className="text-sm font-medium text-zinc-800">{m.display_name}</p>
                      </div>
                      <div className="col-span-3 mt-1 sm:mt-0">
                        <p className="text-xs text-zinc-600 truncate" title={m.email}>{m.email}</p>
                      </div>
                      <div className="col-span-2 mt-1 sm:mt-0">
                        <Badge className={`text-[9px] uppercase rounded-sm ${
                          m.organization_role === "administrator" ? "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20"
                          : m.organization_role === "employee" ? "bg-zinc-100 text-zinc-600 border-zinc-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}>
                          {m.organization_role || "user"}
                        </Badge>
                      </div>
                      <div className="col-span-2 mt-1 sm:mt-0">
                        {(m.cards || []).length === 0 ? (
                          <span className="text-[10px] text-zinc-300">No cards</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {m.cards.map((c) => (
                              <span key={c.card_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-zinc-100 border border-zinc-200">
                                <CreditCard size={9} className="text-zinc-500" />
                                <span className="text-[10px] font-mono font-bold text-zinc-700">****{c.last_four}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="col-span-2 text-right mt-1 sm:mt-0">
                        {m.has_expenser_account ? (
                          <Badge className="text-[9px] uppercase rounded-sm bg-blue-100 text-blue-700 border-blue-200">
                            <Check size={9} weight="bold" className="mr-0.5" /> Invited
                          </Badge>
                        ) : (
                          <Button onClick={() => openInviteDialog(m)} data-testid={`invite-mercury-user-${m.mercury_user_id}`}
                            className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm text-xs h-7 gap-1">
                            <Plus size={12} weight="bold" /> Invite
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <div className="space-y-4">
            {/* QBO */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Database size={18} weight="duotone" className="text-[#002FA7]" />
                  <h3 className="text-sm font-bold text-zinc-800">QuickBooks Online</h3>
                </div>
                <Badge className={`text-[10px] uppercase rounded-sm ${qboStatus.connected ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {qboStatus.connected ? "Connected" : "Not Connected"}
                </Badge>
              </div>

              {qboStatus.connected && qboStatus.company_name && (
                <div className="mb-3 px-3 py-2 rounded-sm bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-800">
                  Connected to <strong>{qboStatus.company_name}</strong> ({qboStatus.environment === "sandbox" ? "Sandbox" : "Production"}, Realm <span className="font-mono">{qboStatus.realm_id}</span>)
                </div>
              )}

              <div className="mb-3 px-3 py-2 rounded-sm bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                <strong>Getting "No sandbox companies found"?</strong> You pasted Development keys. In Intuit Developer Portal switch to the <span className="font-mono">Production</span> tab (next to Development), copy those Client ID + Secret, set Environment below to <strong>Production</strong>, and reconnect.
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Environment</Label>
                  <Select value={qboEnv} onValueChange={setQboEnv}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="qbo-environment">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="production">Production (real QBO company)</SelectItem>
                      <SelectItem value="sandbox">Sandbox (test company)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-400 mt-1">
                    Match this to which keys you pasted below. Intuit Developer Portal has separate
                    <strong> Development</strong> keys (work with Sandbox only) and <strong>Production</strong> keys (work with real QBO companies).
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Client ID</Label>
                  <Input data-testid="qbo-client-id" value={qboClientId} onChange={(e) => setQboClientId(e.target.value)} placeholder="Enter QBO Client ID" className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Client Secret</Label>
                  <Input data-testid="qbo-client-secret" type="password" value={qboClientSecret} onChange={(e) => setQboClientSecret(e.target.value)} placeholder="Enter QBO Client Secret" className="mt-1 rounded-sm" />
                </div>

                <div>
                  <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Redirect URI (register this once in Intuit Developer Portal)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input data-testid="qbo-redirect-uri" readOnly value={qboStatus.redirect_uri || ""} className="rounded-sm font-mono text-[11px] bg-zinc-50" />
                    <Button type="button" variant="outline" onClick={() => copyToClipboard(qboStatus.redirect_uri, "Redirect URI")} data-testid="copy-qbo-redirect-btn"
                      className="rounded-sm gap-1 border-zinc-200 text-xs">
                      <Copy size={12} /> Copy
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-400 mt-1">
                    Intuit Developer Portal → your app → <span className="font-mono">Keys &amp; OAuth</span> → <strong>Production</strong> tab → Redirect URIs → paste this. That's the only setup required; no App Store publication or EULA/Privacy URLs needed.
                  </p>
                </div>

                <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-100">
                  {!qboStatus.connected ? (
                    <Button type="button" onClick={connectQbo} disabled={qboBusy === "connect"} data-testid="qbo-connect-btn"
                      className="bg-[#2CA01C] hover:bg-[#1f7a14] text-white rounded-sm text-xs gap-1.5 shadow-sm">
                      <Database size={12} weight="bold" /> {qboBusy === "connect" ? "Opening Intuit..." : "Connect to QuickBooks"}
                    </Button>
                  ) : (
                    <>
                      <Button type="button" variant="outline" onClick={testQbo} disabled={qboBusy === "test"} data-testid="qbo-test-btn"
                        className="rounded-sm text-xs gap-1.5 border-zinc-200">
                        <Check size={12} /> {qboBusy === "test" ? "Testing..." : "Test Connection"}
                      </Button>
                      <Button type="button" variant="outline" onClick={disconnectQbo} disabled={qboBusy === "disconnect"} data-testid="qbo-disconnect-btn"
                        className="rounded-sm text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50">
                        <Trash size={12} /> {qboBusy === "disconnect" ? "Disconnecting..." : "Disconnect"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* JobTread */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Gear size={18} weight="duotone" className="text-amber-500" />
                  <h3 className="text-sm font-bold text-zinc-800">JobTread</h3>
                </div>
                <Badge className={`text-[10px] uppercase rounded-sm ${settings.jobtread_connected ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {settings.jobtread_connected ? "Connected" : "Not Connected"}
                </Badge>
              </div>
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">API Key</Label>
                <Input data-testid="jt-api-key" type="password" value={jtApiKey} onChange={(e) => setJtApiKey(e.target.value)} placeholder="Enter JobTread API Key" className="mt-1 rounded-sm" />
              </div>
            </div>

            {/* Mercury */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CreditCard size={18} weight="duotone" className="text-emerald-500" />
                  <h3 className="text-sm font-bold text-zinc-800">Mercury Bank</h3>
                </div>
                <Badge className={`text-[10px] uppercase rounded-sm ${settings.mercury_connected ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {settings.mercury_connected ? "Connected" : "Not Connected"}
                </Badge>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">API Key</Label>
                  <Input data-testid="mercury-api-key" type="password" value={mercuryApiKey} onChange={(e) => setMercuryApiKey(e.target.value)} placeholder="Enter Mercury API Key" className="mt-1 rounded-sm" />
                  <p className="text-[10px] text-zinc-400 mt-1">Used for one-time card discovery and historical backfill only. Live transactions arrive via webhook.</p>
                </div>

                {/* Hidden / Tombstoned Transactions — recovery panel */}
                <div className="border-t border-zinc-200 pt-4" data-testid="tombstoned-panel">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Recover Hidden Card Swipes</p>
                    <Button type="button" variant="outline" size="sm" onClick={refreshTombstoned}
                      disabled={tombstonedLoading} data-testid="load-tombstoned-btn"
                      className="rounded-sm text-xs h-7 gap-1 border-zinc-200">
                      {tombstonedLoading ? "Loading…" : (tombstoned.length ? "Refresh" : "Load")}
                    </Button>
                  </div>
                  <p className="text-[11px] text-zinc-500 mb-2 leading-relaxed">
                    Card swipes soft-deleted in the last 30 days &mdash; either by an admin click or by the orphan reconciler.
                    Click Restore to return the row to the Card Swipes queue.
                  </p>
                  {tombstoned.length === 0 && !tombstonedLoading && (
                    <p className="text-[11px] text-zinc-400 italic" data-testid="tombstoned-empty">
                      No hidden swipes to show. Click Load to check.
                    </p>
                  )}
                  {tombstoned.length > 0 && (
                    <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
                      <div className="hidden sm:grid sm:grid-cols-12 gap-2 px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                        <div className="col-span-2">Date</div>
                        <div className="col-span-3">Merchant</div>
                        <div className="col-span-1">Card</div>
                        <div className="col-span-2 text-right">Amount</div>
                        <div className="col-span-2">Removed by</div>
                        <div className="col-span-2 text-right">Action</div>
                      </div>
                      <div className="divide-y divide-zinc-100">
                        {tombstoned.map((row) => (
                          <div key={row.id} className="px-3 py-2 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center hover:bg-zinc-50/60" data-testid={`tombstoned-row-${row.id}`}>
                            <div className="col-span-2 text-xs text-zinc-700">{row.date}</div>
                            <div className="col-span-3 text-xs text-zinc-800 truncate" title={row.merchant}>{row.merchant}</div>
                            <div className="col-span-1 text-[11px] font-mono font-bold text-zinc-700">****{row.card_last_four || "—"}</div>
                            <div className="col-span-2 text-right text-xs font-bold font-mono text-zinc-900">${Number(row.amount).toFixed(2)}</div>
                            <div className="col-span-2 text-[11px] text-zinc-500 truncate">{row.deleted_by_name || row.tombstone_reason || "system"}</div>
                            <div className="col-span-2 text-right">
                              <Button type="button" size="sm" onClick={() => restoreTombstoned(row)}
                                disabled={restoringId === row.id}
                                data-testid={`restore-tombstoned-${row.id}`}
                                className="h-7 rounded-sm text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white gap-1 disabled:opacity-40">
                                {restoringId === row.id ? "Restoring…" : "Restore"}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Webhook section */}
                <div className="border-t border-zinc-200 pt-4">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Live Card Swipe Webhook</p>
                  <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Webhook URL (paste this into Mercury)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input data-testid="mercury-webhook-url" readOnly value={webhookConfig.webhook_url} className="rounded-sm font-mono text-[11px] bg-zinc-50" />
                    <Button type="button" variant="outline" onClick={() => copyToClipboard(webhookConfig.webhook_url, "Webhook URL")} data-testid="copy-webhook-url-btn"
                      className="rounded-sm gap-1 border-zinc-200 text-xs">
                      <Copy size={12} /> Copy
                    </Button>
                  </div>

                  <div className="mt-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Signing Secret (from Mercury)</Label>
                      <Badge className={`text-[9px] uppercase rounded-sm ${webhookConfig.has_secret ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {webhookConfig.has_secret ? "Saved" : "Not Set"}
                      </Badge>
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Input
                        data-testid="mercury-webhook-secret-input"
                        type="password"
                        autoComplete="off"
                        value={webhookSecretInput}
                        onChange={(e) => setWebhookSecretInput(e.target.value)}
                        placeholder={webhookConfig.has_secret ? "Paste new secret to replace existing" : "Paste the secret Mercury gave you (whsec_…)"}
                        className="rounded-sm font-mono text-[11px]"
                      />
                      <Button type="button" onClick={saveWebhookSecret} data-testid="save-webhook-secret-btn"
                        disabled={!webhookSecretInput.trim()}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm text-xs gap-1.5 disabled:opacity-40">
                        <Check size={12} weight="bold" /> Save
                      </Button>
                      {webhookConfig.has_secret && (
                        <Button type="button" variant="outline" onClick={clearWebhookSecret} data-testid="clear-webhook-secret-btn"
                          className="rounded-sm text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50">
                          <Trash size={12} /> Clear
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1">
                      {webhookConfig.has_secret
                        ? "Secret is stored. Paste a new one above only if you re-create the webhook in Mercury."
                        : "Without this, Expenser won't be able to verify webhook events."}
                    </p>
                  </div>

                  {webhookHealth && (() => {
                    // Compute health color: green if success in last 24h with no failures,
                    // yellow if 24-72h or has failures, red if no success in 72h or secret missing.
                    const now = Date.now();
                    const lastSuccess = webhookHealth.last_success_at ? new Date(webhookHealth.last_success_at).getTime() : 0;
                    const lastReceived = webhookHealth.last_received_at ? new Date(webhookHealth.last_received_at).getTime() : 0;
                    const minsSinceSuccess = lastSuccess ? Math.floor((now - lastSuccess) / 60000) : null;
                    const minsSinceReceived = lastReceived ? Math.floor((now - lastReceived) / 60000) : null;
                    const has72hSuccess = lastSuccess && (now - lastSuccess) < 72 * 3600 * 1000;
                    const has24hSuccess = lastSuccess && (now - lastSuccess) < 24 * 3600 * 1000;
                    const failures = webhookHealth.failures_7d_count || 0;
                    let color, label, hint;
                    if (!webhookHealth.secret_configured) {
                      color = "red";
                      label = "Secret missing";
                      hint = "Webhook signature verification is disabled. Save a secret above.";
                    } else if (!lastReceived) {
                      color = "amber";
                      label = "No webhooks received yet";
                      hint = "Either no swipes have occurred or Mercury's webhook config is wrong.";
                    } else if (has24hSuccess && failures === 0) {
                      color = "emerald";
                      label = "Healthy";
                      hint = `Last verified webhook ${minsSinceSuccess < 60 ? `${minsSinceSuccess}m` : `${Math.floor(minsSinceSuccess / 60)}h`} ago.`;
                    } else if (has72hSuccess || (failures > 0 && lastSuccess)) {
                      color = "amber";
                      label = `${failures} failure(s) in 7d`;
                      hint = lastSuccess
                        ? `Last verified webhook ${minsSinceSuccess < 60 ? `${minsSinceSuccess}m` : `${Math.floor(minsSinceSuccess / 60)}h`} ago. Mercury delivery may be intermittent.`
                        : "Most recent webhooks failed signature verification — check Mercury's webhook secret matches.";
                    } else {
                      color = "red";
                      label = "Broken";
                      hint = `Last successful verify >72h ago. Mercury is delivering but signatures are mismatching. Rotate the secret and re-paste into Mercury.`;
                    }
                    const colors = {
                      emerald: { dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
                      amber: { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
                      red: { dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
                    }[color];
                    return (
                      <div data-testid="webhook-health-widget" className={`mt-3 rounded-sm border px-3 py-2 ${colors.border} ${colors.bg}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block w-2 h-2 rounded-full ${colors.dot} animate-pulse`} />
                            <span className={`text-[11px] font-semibold uppercase tracking-wider ${colors.text}`}>Webhook health: {label}</span>
                          </div>
                          <button
                            type="button"
                            onClick={refreshWebhookHealth}
                            className="text-[10px] text-zinc-500 hover:text-zinc-900 underline"
                            data-testid="webhook-health-refresh"
                          >
                            Refresh
                          </button>
                        </div>
                        <p className={`text-[11px] ${colors.text}`}>{hint}</p>
                        <div className="grid grid-cols-3 gap-2 mt-2 text-[10px]">
                          <div>
                            <div className="text-zinc-500 uppercase tracking-wider">Last received</div>
                            <div className="text-zinc-900 font-medium">
                              {minsSinceReceived === null
                                ? "—"
                                : minsSinceReceived < 60
                                ? `${minsSinceReceived}m ago`
                                : minsSinceReceived < 1440
                                ? `${Math.floor(minsSinceReceived / 60)}h ago`
                                : `${Math.floor(minsSinceReceived / 1440)}d ago`}
                            </div>
                          </div>
                          <div>
                            <div className="text-zinc-500 uppercase tracking-wider">Last verified</div>
                            <div className="text-zinc-900 font-medium">
                              {minsSinceSuccess === null
                                ? "—"
                                : minsSinceSuccess < 60
                                ? `${minsSinceSuccess}m ago`
                                : minsSinceSuccess < 1440
                                ? `${Math.floor(minsSinceSuccess / 60)}h ago`
                                : `${Math.floor(minsSinceSuccess / 1440)}d ago`}
                            </div>
                          </div>
                          <div>
                            <div className="text-zinc-500 uppercase tracking-wider">Failures 7d</div>
                            <div className={`font-medium ${failures > 0 ? "text-red-600" : "text-zinc-900"}`}>{failures}</div>
                          </div>
                        </div>
                        {Object.keys(webhookHealth.failures_by_reason_7d || {}).length > 0 && (
                          <div className="mt-2 text-[10px] text-zinc-600">
                            <span className="text-zinc-500">By reason:</span>{" "}
                            {Object.entries(webhookHealth.failures_by_reason_7d).map(([r, n]) => (
                              <span key={r} className="mr-2 font-mono">
                                {r}={n}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Webhook delivery drift — surfaces the "Mercury silently
                            timed out and we never saw the event" case that the
                            health metric above can't detect. Probe runs every 5
                            min server-side. */}
                        {(() => {
                          const drift = webhookHealth.drift || {};
                          if (!drift.available) return null;
                          const missing = drift.missing_count || 0;
                          const oldest = drift.oldest_missing_at;
                          let oldestText = "";
                          if (oldest) {
                            const mins = Math.max(0, Math.floor((Date.now() - new Date(oldest).getTime()) / 60000));
                            oldestText = mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
                          }
                          const driftColor = missing === 0
                            ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                            : missing < 3
                            ? "text-amber-700 border-amber-200 bg-amber-50"
                            : "text-red-700 border-red-200 bg-red-50";
                          return (
                            <div className={`mt-2 rounded-sm border px-2 py-1.5 text-[10px] ${driftColor}`} data-testid="webhook-drift-row">
                              <div className="flex items-center justify-between">
                                <span className="font-semibold uppercase tracking-wider">Delivery drift</span>
                                <span className="text-[9px] opacity-70">
                                  {drift.checked_at ? `checked ${Math.max(0, Math.floor((Date.now() - new Date(drift.checked_at).getTime()) / 60000))}m ago` : ""}
                                </span>
                              </div>
                              {missing === 0 ? (
                                <p className="mt-0.5">
                                  In sync with Mercury — last {drift.mercury_recent_count || 0} card txns all received via webhook.
                                </p>
                              ) : (
                                <>
                                  <p className="mt-0.5">
                                    <span className="font-bold">{missing}</span> card txn{missing === 1 ? "" : "s"} visible in Mercury but not received via webhook
                                    {oldestText ? ` (oldest ${oldestText})` : ""}. Mercury may be timing out — sync will run on the hourly backstop.
                                  </p>
                                  {(drift.sample_missing || []).length > 0 && (
                                    <ul className="mt-1 ml-3 list-disc space-y-0.5">
                                      {(drift.sample_missing || []).slice(0, 5).map((m) => (
                                        <li key={m.mercury_id} className="font-mono text-[9px]">
                                          {m.merchant} · ${Math.abs(m.amount || 0).toFixed(2)} · {m.created_at?.slice(0, 16).replace("T", " ")}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  <div className="mt-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                    <strong>Mercury returns 401 when adding the webhook?</strong> Click <span className="font-mono">Clear</span> first so our endpoint accepts Mercury's test ping. After Mercury creates the webhook and shows you the secret, paste it here and Save.
                  </div>

                  <ol className="mt-4 text-[11px] text-zinc-500 space-y-1 list-decimal list-inside">
                    <li>If a secret is already saved, click <span className="font-mono text-zinc-700">Clear</span> first</li>
                    <li>In Mercury go to <span className="font-mono text-zinc-700">Settings → Developers → Webhooks</span> and click <span className="font-mono text-zinc-700">Create Webhook</span></li>
                    <li>Paste the <strong>Webhook URL</strong> above into Mercury</li>
                    <li>Subscribe to <span className="font-mono text-zinc-700">card transaction</span> events and create the webhook</li>
                    <li>Mercury will show a <strong>signing secret once</strong> (starts with <span className="font-mono text-zinc-700">whsec_</span>) — copy it</li>
                    <li>Paste it into the <strong>Signing Secret</strong> field above and click Save</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-zinc-100">
              <Button onClick={saveIntegrations} data-testid="save-integrations-btn"
                className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm shadow-sm">
                Save Integration Settings
              </Button>
              <div className="flex-1" />
              <Button onClick={handleResetPush} variant="outline" data-testid="reset-push-btn"
                className="rounded-sm text-xs border-zinc-300 hover:bg-zinc-50 text-zinc-700 gap-1.5">
                Reset notifications
              </Button>
              <Button onClick={purgeSimulated} variant="outline" data-testid="purge-simulated-btn"
                className="rounded-sm text-xs border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 gap-1.5">
                <Lightning size={13} weight="fill" className="text-amber-500" />
                Purge all training data
              </Button>
              <Button onClick={purgePreCutoff} variant="outline" data-testid="purge-pre-cutoff-btn"
                className="rounded-sm text-xs border-red-300 bg-red-50 hover:bg-red-100 text-red-900 gap-1.5"
                title="Removes Mercury transactions dated before the May 1, 2026 launch cutoff.">
                <Lightning size={13} weight="fill" className="text-red-500" />
                Purge pre-launch txns
              </Button>
              <Button onClick={purgeZeroAmount} variant="outline" data-testid="purge-zero-amount-btn"
                className="rounded-sm text-xs border-red-300 bg-red-50 hover:bg-red-100 text-red-900 gap-1.5"
                title="Removes phantom $0 Mercury transactions (pre-auth/memo events).">
                <Lightning size={13} weight="fill" className="text-red-500" />
                Purge $0 phantoms
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* GL Accounts Tab */}
        <TabsContent value="gl-accounts">
          <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                  GL Accounts / Categories — {glAccounts.filter((a) => a.visible !== false).length} of {glAccounts.length} visible
                </h3>
                {qboStatus.last_account_sync_at && (
                  <p className="text-[10px] text-zinc-400 mt-0.5">
                    Last synced from QuickBooks: {new Date(qboStatus.last_account_sync_at).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={syncGLFromQBO} disabled={syncing === "qbo-gl" || !qboStatus.connected} data-testid="sync-qbo-gl-btn"
                  className="bg-[#2CA01C] hover:bg-[#1f7a14] text-white rounded-sm text-xs gap-1.5 h-7 disabled:opacity-40">
                  <ArrowsClockwise size={12} weight="bold" className={syncing === "qbo-gl" ? "animate-spin" : ""} />
                  {syncing === "qbo-gl" ? "Syncing..." : "Sync from QuickBooks"}
                </Button>
                <Button variant="outline" onClick={() => bulkSetGLVisibility(true)} data-testid="gl-show-all-btn"
                  className="rounded-sm text-xs gap-1 border-zinc-200 h-7">
                  Show all
                </Button>
                <Button variant="outline" onClick={() => bulkSetGLVisibility(false)} data-testid="gl-hide-all-btn"
                  className="rounded-sm text-xs gap-1 border-zinc-200 h-7">
                  Hide all
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-zinc-400 mb-3">
              Toggle visibility to control which accounts employees can pick when coding an expense. Toggle <strong>Auto</strong> to auto-approve expenses coded fully to that overhead account (requires a matched QBO vendor). {qboStatus.connected ? "Click Sync from QuickBooks to pull in any new or edited accounts — runs automatically every 6 hours." : "Connect QuickBooks (Integrations tab) to pull your real chart of accounts."}
            </p>
            <div className="relative mb-4">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input
                data-testid="gl-search"
                value={glSearch}
                onChange={(e) => setGlSearch(e.target.value)}
                placeholder="Search by name, account number, or type..."
                className="rounded-sm pl-8"
              />
              {glSearch && (
                <button
                  type="button"
                  onClick={() => setGlSearch("")}
                  data-testid="clear-gl-search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider font-bold text-zinc-400 hover:text-zinc-700"
                >
                  Clear
                </button>
              )}
            </div>
            {glAccounts.length === 0 ? (
              <div className="text-center py-10 text-sm text-zinc-400">
                No GL accounts yet. {qboStatus.connected ? "Click \"Sync from QuickBooks\" above to pull your chart of accounts." : "Connect QuickBooks first (Integrations tab)."}
              </div>
            ) : (() => {
                const q = glSearch.trim().toLowerCase();
                const filtered = q
                  ? glAccounts.filter((a) =>
                      (a.name || "").toLowerCase().includes(q)
                      || (a.account_number || "").toLowerCase().includes(q)
                      || (a.account_type || "").toLowerCase().includes(q)
                      || (a.account_subtype || "").toLowerCase().includes(q)
                    )
                  : glAccounts;
                // Group by account_type
                const groups = {};
                filtered.forEach((a) => {
                  const key = a.account_type || "Uncategorized";
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(a);
                });
                // Sort each group by account_number then name
                Object.values(groups).forEach((arr) => arr.sort((x, y) => {
                  const nx = x.account_number || "";
                  const ny = y.account_number || "";
                  if (nx && ny) return nx.localeCompare(ny, undefined, { numeric: true });
                  if (nx) return -1;
                  if (ny) return 1;
                  return (x.name || "").localeCompare(y.name || "");
                }));
                const groupOrder = Object.keys(groups).sort();
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-10 text-sm text-zinc-400" data-testid="gl-no-results">
                      No accounts match "{glSearch}"
                    </div>
                  );
                }
                return (
                  <div className="space-y-3">
                    {groupOrder.map((type) => {
                      const items = groups[type];
                      const visibleCount = items.filter((a) => a.visible !== false).length;
                      const collapsed = !!collapsedGroups[type];
                      return (
                        <div key={type} className="border border-zinc-200 rounded-sm overflow-hidden" data-testid={`gl-group-${type}`}>
                          <button
                            type="button"
                            onClick={() => setCollapsedGroups((p) => ({ ...p, [type]: !collapsed }))}
                            data-testid={`gl-group-toggle-${type}`}
                            className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 hover:bg-zinc-100 transition-colors border-b border-zinc-200"
                          >
                            <div className="flex items-center gap-2">
                              {collapsed ? <CaretRight size={12} weight="bold" className="text-zinc-500" /> : <CaretDown size={12} weight="bold" className="text-zinc-500" />}
                              <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-zinc-700">{type}</span>
                            </div>
                            <span className="text-[10px] text-zinc-500 font-medium">{visibleCount} of {items.length} visible</span>
                          </button>
                          {!collapsed && (
                            <div className="divide-y divide-zinc-100">
                              {items.map((acc) => {
                                const isVisible = acc.visible !== false;
                                const autoOn = !!acc.auto_approve;
                                return (
                                  <div key={acc.id} className="py-2.5 px-3 flex items-center gap-3" data-testid={`gl-row-${acc.id}`}>
                                    <Switch
                                      checked={isVisible}
                                      onCheckedChange={() => toggleGLVisibility(acc.id)}
                                      data-testid={`gl-visibility-${acc.id}`}
                                      className="flex-shrink-0"
                                    />
                                    <div className="min-w-0 flex-1 flex items-baseline gap-2 flex-wrap">
                                      {acc.account_number && <span className="text-[11px] font-mono text-zinc-400 w-12">{acc.account_number}</span>}
                                      <span className={`text-sm font-medium ${isVisible ? "text-zinc-800" : "text-zinc-400"}`}>{acc.name}</span>
                                      {acc.account_subtype && acc.account_subtype !== acc.account_type && (
                                        <Badge className="text-[9px] uppercase rounded-sm bg-zinc-100 text-zinc-500 font-normal">{acc.account_subtype}</Badge>
                                      )}
                                      {acc.active === false && (
                                        <Badge className="text-[9px] uppercase rounded-sm bg-red-50 text-red-500 font-normal border border-red-100">Inactive in QBO</Badge>
                                      )}
                                      {autoOn && (
                                        <Badge className="text-[9px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200" title="Expenses coded entirely to this overhead account auto-approve on submit">
                                          auto-approve
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0" title="Auto-approve: expenses fully coded to this overhead account skip the approval queue (vendor must be matched to a QBO vendor)">
                                      <span className="text-[9px] uppercase tracking-[0.12em] font-bold text-zinc-400 hidden sm:inline">Auto</span>
                                      <Switch
                                        checked={autoOn}
                                        onCheckedChange={() => toggleGLAutoApprove(acc.id)}
                                        data-testid={`gl-auto-approve-${acc.id}`}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
          </div>
        </TabsContent>

        {/* Jobs Tab */}
        <TabsContent value="jobs">
          <div className="space-y-4">
            {/* Sync from JobTread + QBO */}
            <div className="flex gap-2 flex-wrap">
              <Button onClick={syncJTJobs} disabled={syncing === "jobs"} data-testid="sync-jt-jobs-btn"
                className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm text-xs gap-1.5 shadow-sm">
                <ArrowsClockwise size={14} className={syncing === "jobs" ? "animate-spin" : ""} />
                {syncing === "jobs" ? "Syncing Jobs..." : "Sync Jobs from JobTread"}
              </Button>
              <Button onClick={syncJTBudgetItems} disabled={syncing === "budget"} variant="outline" data-testid="sync-jt-budget-btn"
                className="rounded-sm text-xs gap-1.5 border-zinc-200">
                <ArrowsClockwise size={14} className={syncing === "budget" ? "animate-spin" : ""} />
                {syncing === "budget" ? "Syncing Budget Items..." : "Sync Budget Items"}
              </Button>
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5">
              <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400 mb-3">Construction Jobs ({jobs.length})</h3>
              <p className="text-[10px] text-zinc-400 mb-3 leading-relaxed">
                Each job must be mapped to a QuickBooks customer/project so approved expenses post under the right job costing. Jobs are auto-matched on QBO Customer sync; pick from the dropdown to override.
              </p>
              <div className="flex gap-2 mb-4">
                <Input data-testid="new-job-name" value={newJob.name} onChange={(e) => setNewJob({ ...newJob, name: e.target.value })} placeholder="Job name" className="rounded-sm flex-1" />
                <Input data-testid="new-job-number" value={newJob.number} onChange={(e) => setNewJob({ ...newJob, number: e.target.value })} placeholder="Job #" className="rounded-sm w-24" />
                <Input data-testid="new-job-customer" value={newJob.customer} onChange={(e) => setNewJob({ ...newJob, customer: e.target.value })} placeholder="Customer" className="rounded-sm w-32" />
                <Button onClick={addJob} data-testid="add-job-btn" className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm gap-1">
                  <Plus size={14} /> Add
                </Button>
              </div>
              <div className="divide-y divide-zinc-100">
                {jobs.length > 0 ? jobs.map((job) => {
                  const linkedCust = qboCustomers.find((c) => c.qbo_id === job.qbo_customer_id);
                  return (
                    <div key={job.id} className="py-2.5 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center" data-testid={`job-row-${job.id}`}>
                      <div className="sm:col-span-5 flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-800">{job.name}</span>
                        {job.number && <span className="text-xs text-zinc-400">{job.number}</span>}
                        {job.customer && <span className="text-xs text-zinc-400">/ {job.customer}</span>}
                        {job.jobtread_id && <Badge className="text-[9px] uppercase rounded-sm bg-blue-50 text-blue-600 border-blue-200">JT</Badge>}
                      </div>
                      <div className="sm:col-span-6 mt-1 sm:mt-0">
                        <Select value={job.qbo_customer_id || "__none__"} onValueChange={(v) => updateJobCustomer(job.id, v === "__none__" ? "" : v)}>
                          <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`job-qbo-customer-${job.id}`}>
                            <SelectValue placeholder="— Map to QBO customer/project —">
                              {linkedCust ? (
                                <span className="flex items-center gap-1.5">
                                  {linkedCust.is_project && <Badge className="text-[8px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200 px-1">project</Badge>}
                                  <span className="truncate">{linkedCust.fully_qualified_name || linkedCust.display_name}</span>
                                </span>
                              ) : job.qbo_customer_id ? `QBO #${job.qbo_customer_id}` : "— Unmapped —"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__"><span className="text-zinc-400">— Unmapped —</span></SelectItem>
                            {qboCustomers.map((c) => (
                              <SelectItem key={c.qbo_id} value={c.qbo_id}>
                                <span className="flex items-center gap-1.5">
                                  {c.is_project && <Badge className="text-[8px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200 px-1">project</Badge>}
                                  <span className="truncate">{c.fully_qualified_name || c.display_name}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-1 text-right mt-1 sm:mt-0">
                        <Button variant="ghost" size="sm" onClick={() => deleteJob(job.id)} data-testid={`delete-job-${job.id}`}
                          className="h-7 px-2 text-red-400 hover:text-red-600">
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="py-6 text-center text-sm text-zinc-400">
                    No jobs yet. Click &quot;Sync Jobs from JobTread&quot; or add manually.
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* QBO Customers Tab */}
        <TabsContent value="customers">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                  QBO Customers &amp; Projects &mdash; {qboCustomers.length}
                </h3>
                <p className="text-[10px] text-zinc-400 mt-0.5 leading-relaxed">
                  Pulled from QuickBooks. Customers and Projects (sub&#8209;customers) become the <span className="text-zinc-500 font-medium">CustomerRef</span> on each pushed Purchase line. Map your Jobs to these in the Jobs tab.
                </p>
              </div>
              <Button onClick={syncQboCustomers} disabled={syncing === "qbo-customers" || !qboStatus.connected}
                data-testid="sync-qbo-customers-btn"
                className="bg-[#2CA01C] hover:bg-[#1f7a14] text-white rounded-sm text-xs gap-1.5 shadow-sm disabled:opacity-40 h-8">
                <ArrowsClockwise size={14} className={syncing === "qbo-customers" ? "animate-spin" : ""} />
                {syncing === "qbo-customers" ? "Syncing..." : "Sync from QBO"}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <MagnifyingGlass size={14} className="text-zinc-400" />
              <Input
                data-testid="customer-search"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search customers and projects..."
                className="rounded-sm h-8 text-xs"
              />
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
              <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                <div className="col-span-7">Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-3">QBO ID</div>
              </div>
              <div className="divide-y divide-zinc-100 max-h-[60vh] overflow-y-auto">
                {qboCustomers.length === 0 ? (
                  <div className="p-8 text-center text-sm text-zinc-400">
                    Click &quot;Sync from QBO&quot; to pull customers and projects.
                  </div>
                ) : (
                  qboCustomers
                    .filter((c) => {
                      if (!customerSearch) return true;
                      const q = customerSearch.toLowerCase();
                      return (c.fully_qualified_name || c.display_name || "").toLowerCase().includes(q);
                    })
                    .map((c) => (
                      <div key={c.qbo_id} className="px-4 py-2.5 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center hover:bg-zinc-50 transition-colors" data-testid={`customer-row-${c.qbo_id}`}>
                        <div className="col-span-7">
                          <p className="text-sm font-medium text-zinc-800 truncate">{c.fully_qualified_name || c.display_name}</p>
                          {c.company_name && c.company_name !== c.display_name && (
                            <p className="text-[10px] text-zinc-400 truncate">{c.company_name}</p>
                          )}
                        </div>
                        <div className="col-span-2 mt-1 sm:mt-0">
                          {c.is_project ? (
                            <Badge className="text-[9px] uppercase rounded-sm bg-emerald-50 text-emerald-700 border-emerald-200">Project</Badge>
                          ) : (
                            <Badge className="text-[9px] uppercase rounded-sm bg-blue-50 text-blue-700 border-blue-200">Customer</Badge>
                          )}
                        </div>
                        <div className="col-span-3 mt-1 sm:mt-0">
                          <span className="text-[11px] font-mono text-zinc-500">#{c.qbo_id}</span>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* QBO Vendors Tab */}
        <TabsContent value="vendors">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                  QBO Vendors &mdash; {qboVendors.length}
                </h3>
                <p className="text-[10px] text-zinc-400 mt-0.5 leading-relaxed">
                  Pulled from QuickBooks. When an expense&apos;s vendor name matches one here, the pushed Purchase will carry the <span className="text-zinc-500 font-medium">EntityRef</span> for proper vendor reporting.
                </p>
              </div>
              <Button onClick={syncQboVendors} disabled={syncing === "qbo-vendors" || !qboStatus.connected}
                data-testid="sync-qbo-vendors-btn"
                className="bg-[#2CA01C] hover:bg-[#1f7a14] text-white rounded-sm text-xs gap-1.5 shadow-sm disabled:opacity-40 h-8">
                <ArrowsClockwise size={14} className={syncing === "qbo-vendors" ? "animate-spin" : ""} />
                {syncing === "qbo-vendors" ? "Syncing..." : "Sync from QBO"}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <MagnifyingGlass size={14} className="text-zinc-400" />
              <Input
                data-testid="vendor-search"
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                placeholder="Search vendors..."
                className="rounded-sm h-8 text-xs"
              />
            </div>

            <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
              <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                <div className="col-span-9">Name</div>
                <div className="col-span-3">QBO ID</div>
              </div>
              <div className="divide-y divide-zinc-100 max-h-[60vh] overflow-y-auto">
                {qboVendors.length === 0 ? (
                  <div className="p-8 text-center text-sm text-zinc-400">
                    Click &quot;Sync from QBO&quot; to pull vendors.
                  </div>
                ) : (
                  qboVendors
                    .filter((v) => {
                      if (!vendorSearch) return true;
                      return (v.name || "").toLowerCase().includes(vendorSearch.toLowerCase());
                    })
                    .map((v) => (
                      <div key={v.id || v.qbo_id} className="px-4 py-2.5 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center hover:bg-zinc-50 transition-colors" data-testid={`vendor-row-${v.qbo_id || v.id}`}>
                        <div className="col-span-9">
                          <p className="text-sm font-medium text-zinc-800 truncate">{v.name}</p>
                          {v.company_name && v.company_name !== v.name && (
                            <p className="text-[10px] text-zinc-400 truncate">{v.company_name}</p>
                          )}
                        </div>
                        <div className="col-span-3 mt-1 sm:mt-0">
                          {v.qbo_id ? (
                            <span className="text-[11px] font-mono text-zinc-500">#{v.qbo_id}</span>
                          ) : (
                            <span className="text-[10px] text-amber-600 italic">unlinked</span>
                          )}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Invite Mercury User Dialog */}
      <Dialog open={inviteDialog.open} onOpenChange={(o) => !o && setInviteDialog({ open: false, employee: null, email: "", name: "", role: "submitter", sending: false, result: null })}>
        <DialogContent className="rounded-sm max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Invite to Expenser
            </DialogTitle>
            <DialogDescription>
              Send {inviteDialog.name || "this employee"} an email with a link to set their password.
            </DialogDescription>
          </DialogHeader>
          {!inviteDialog.result ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Invite Email</Label>
                <Input value={inviteDialog.email} onChange={(e) => setInviteDialog((p) => ({ ...p, email: e.target.value }))}
                  data-testid="invite-dialog-email" className="mt-1 rounded-sm" type="email" placeholder="employee@c3-contracting.com" required />
                <p className="text-[10px] text-zinc-400 mt-1 leading-relaxed">
                  {inviteDialog.employee?.email ? (
                    <>Mercury has <span className="font-mono text-zinc-500">{inviteDialog.employee.email}</span>. Override above if you'd rather send the invite somewhere else.</>
                  ) : (
                    <>No email on file in Mercury — type one to send the invite.</>
                  )}
                </p>
              </div>
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Display name</Label>
                <Input value={inviteDialog.name} onChange={(e) => setInviteDialog((p) => ({ ...p, name: e.target.value }))}
                  data-testid="invite-dialog-name" className="mt-1 rounded-sm" required />
              </div>
              <div>
                <Label className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Role</Label>
                <Select value={inviteDialog.role} onValueChange={(v) => setInviteDialog((p) => ({ ...p, role: v }))}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="invite-dialog-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label} &mdash; {o.description}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={sendInvite} disabled={!inviteDialog.email || !inviteDialog.name || inviteDialog.sending}
                  data-testid="send-invite-btn"
                  className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm flex-1">
                  {inviteDialog.sending ? "Sending..." : "Send invite email"}
                </Button>
                <Button variant="outline" onClick={() => setInviteDialog({ open: false, employee: null, email: "", name: "", role: "submitter", sending: false, result: null })}
                  className="rounded-sm border-zinc-200">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="invite-result">
              {inviteDialog.result.email?.sent ? (
                <div className="px-3 py-2 rounded-sm bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-800">
                  Invite email sent to <strong>{inviteDialog.result.user.email}</strong>. They'll set their password from the link in the email.
                </div>
              ) : (
                <div className="px-3 py-2 rounded-sm bg-amber-50 border border-amber-200 text-[12px] text-amber-800">
                  Email could not be delivered ({inviteDialog.result.email?.reason}). Share this link with the employee directly — it expires in 7 days:
                </div>
              )}
              <div className="flex gap-2">
                <Input readOnly value={inviteDialog.result.invite_url} className="rounded-sm font-mono text-[11px] bg-zinc-50" data-testid="invite-url" />
                <Button variant="outline" onClick={() => copyToClipboard(inviteDialog.result.invite_url, "Invite link")}
                  className="rounded-sm border-zinc-200 text-xs gap-1">
                  <Copy size={12} /> Copy
                </Button>
              </div>
              <Button onClick={() => setInviteDialog({ open: false, employee: null, email: "", name: "", role: "submitter", sending: false, result: null })}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto flex flex-col">
          {/* max-h + overflow-y-auto fixes iOS/small-viewport overflow where the
              dialog was clipped at both edges with no way to scroll (2026-06-22). */}
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Edit User
            </DialogTitle>
            <DialogDescription>Map Mercury identity and each card&apos;s QBO source account for outbound push.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Name (Preferred)</Label>
                <Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Roles</Label>
                <p className="text-[10px] text-zinc-400 mt-1 mb-2 leading-relaxed">Submitter is implicit for everyone. Toggle Administrator and Approver independently. <span className="text-amber-700 font-medium">Accountant is a standalone read-only role</span> — picking it clears the others (and vice versa).</p>
                <div className="grid grid-cols-2 gap-2">
                  {ROLE_OPTIONS.filter((o) => o.value !== "submitter").map((o) => {
                    const active = (editForm.roles || []).includes(o.value);
                    return (
                      <button
                        type="button"
                        key={o.value}
                        onClick={() => toggleEditRole(o.value)}
                        data-testid={`edit-user-role-${o.value}`}
                        className={`px-3 py-2 rounded-sm border text-left transition-colors ${
                          active
                            ? "bg-[#002FA7]/10 border-[#002FA7] text-[#002FA7]"
                            : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold uppercase tracking-wider">{o.label}</span>
                          {active && <Check size={12} weight="bold" />}
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-0.5 leading-snug">{o.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-user-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Auto-approve</Label>
                  <div className="mt-1 flex items-center gap-2 h-10 px-3 rounded-sm border border-zinc-200 bg-white">
                    <Switch
                      checked={!!editForm.auto_approve}
                      onCheckedChange={(v) => setEditForm({ ...editForm, auto_approve: v })}
                      disabled={(editForm.roles || []).includes("accountant")}
                      data-testid="edit-user-auto-approve"
                    />
                    <span className="text-xs text-zinc-600">
                      {(editForm.roles || []).includes("accountant")
                        ? "N/A — read-only role"
                        : editForm.auto_approve ? "Skip approval queue" : "Use approval queue"}
                    </span>
                  </div>
                </div>
              </div>
              {editForm.auto_approve && (
                <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm leading-relaxed" data-testid="auto-approve-note">
                  ⚠ Submissions from this user will skip the approval queue and push directly to QuickBooks <strong>only if the vendor is matched to a QBO vendor</strong>. Unmatched vendors still fall through to a human approver.
                </p>
              )}
              <div className="border-t border-zinc-200 pt-3">
                <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Mercury Mapping</p>
                <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Mercury User ID</Label>
                <Input data-testid="edit-mercury-id" value={editForm.mercury_user_id} onChange={(e) => setEditForm({ ...editForm, mercury_user_id: e.target.value })} placeholder="Auto-filled when you sync the Mercury Team" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              {(editUser.cards || []).length > 0 && (
                <div className="border-t border-zinc-200 pt-3">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1">Card → QBO Bank/Credit Account</p>
                  <p className="text-[10px] text-zinc-400 mb-2 leading-relaxed">Each card needs a QBO bank or credit card account — this is the account the Purchase debits/credits in QuickBooks when an expense is approved.</p>
                  <div className="space-y-2">
                    {editUser.cards.map((c) => {
                      const isBankOrCard = (a) => a.account_type === "Bank" || a.account_type === "Credit Card";
                      const cardAcctItems = [
                        { value: "__none__", label: "— Unmapped —" },
                        ...buildGlItems(glAccounts, { filter: isBankOrCard, selectedIds: [c.qbo_account_id] }).map((it) => {
                          const t = glAccounts.find((a) => a.id === it.value)?.account_type;
                          return { ...it, suffix: (
                            <Badge className={`ml-2 text-[8px] uppercase rounded-sm px-1 shrink-0 ${t === "Bank" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-purple-50 text-purple-700 border-purple-200"}`}>
                              {t === "Bank" ? "bank" : "credit"}
                            </Badge>
                          ) };
                        }),
                      ];
                      return (
                        <div key={c.card_id} className="flex items-center gap-2" data-testid={`card-qbo-mapping-${c.card_id}`}>
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-zinc-100 border border-zinc-200 shrink-0">
                            <CreditCard size={11} className="text-zinc-500" />
                            <span className="text-[11px] font-mono font-bold text-zinc-700">****{c.last_four}</span>
                            {c.card_type === "credit" && <span className="text-[8px] uppercase tracking-wider text-purple-600 font-bold">credit</span>}
                            {c.card_type === "deposit" && <span className="text-[8px] uppercase tracking-wider text-blue-600 font-bold">debit</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <SearchableSelect
                              value={c.qbo_account_id || "__none__"}
                              onChange={(v) => updateCardQboAccount(editUser.id, c, v === "__none__" ? "" : v)}
                              items={cardAcctItems}
                              placeholder="— Map to QBO account —"
                              searchPlaceholder="Search bank & card accounts..."
                              size="sm"
                              wrap
                              testId={`card-qbo-acct-trigger-${c.card_id}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Manually add a card by last-four (used for cancelled/former-employee
                  cards that don't appear in Mercury's card discovery but still have
                  orphaned expenses/refunds flowing in). */}
              <div className="border-t border-zinc-200 pt-3" data-testid="manual-add-card-section">
                <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1">Manually Attach a Card</p>
                <p className="text-[10px] text-zinc-400 mb-2 leading-relaxed">
                  Use when a former-employee card (or any cancelled/expired card) has an orphaned expense or refund. Just enter the last 4 digits — the card will attach to this user and appear in the list above where you can then set its QBO account.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="text" inputMode="numeric" maxLength={4}
                    placeholder="Last 4 (e.g. 3491)"
                    value={manualCard.last_four}
                    onChange={(e) => setManualCard({ ...manualCard, last_four: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    className="rounded-sm h-8 text-xs w-32 font-mono"
                    data-testid="manual-card-last-four-input"
                  />
                  <Select value={manualCard.card_type} onValueChange={(v) => setManualCard({ ...manualCard, card_type: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-28" data-testid="manual-card-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Credit</SelectItem>
                      <SelectItem value="deposit">Debit</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="button" onClick={addManualCard}
                    disabled={!/^\d{4}$/.test(manualCard.last_four || "")}
                    data-testid="manual-add-card-btn"
                    className="h-8 rounded-sm text-xs bg-[#002FA7] hover:bg-[#002280] text-white gap-1 disabled:opacity-40">
                    <Check size={12} weight="bold" /> Attach card
                  </Button>
                </div>
              </div>
              <Button onClick={saveUserMapping} data-testid="save-user-btn"
                className="w-full bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm gap-1.5">
                <Check size={14} weight="bold" /> Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
